import type {
	BoundActions,
	ContainerComponent,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	StoreActionApi,
} from 'react-sweet-state';
import {
	createHook,
	createActionsHook,
	createStateHook,
	createStore,
	createContainer,
} from 'react-sweet-state';

import type {
	CollabTelepointerPayload,
	PresenceActivity,
	ProviderParticipant,
} from '@atlaskit/editor-common/collab';
import { getParticipantColor } from '@atlaskit/editor-shared-styles/utils';
import type { AnonymousAsset } from '@atlaskit/anonymous-assets/types';

import type { PresenceParticipant, PresenceSessionData } from './index.ts';

import { UNIDENTIFIED, getActivityToDisplay, getCurrentUserFromMap } from '../presenceUtils.ts';
import type { Participant } from '../presenceTypes.ts';

type TelepointerData = CollabTelepointerPayload & { timestamp: number };
type PresenceStoreContainerProps = {
	participant?: PresenceParticipant;
	fetchAnonymousAsset(presenceId: string | undefined): Promise<AnonymousAsset | undefined>;
	onError(err: Error): void;
};

interface PresenceStoreState {
	participants: Map<string, PresenceParticipant>;
	telepointers: Map<string, TelepointerData>;
}

// yes, all this is hella messy right now, will be refactoring and moving out of a store
const PresenceStore = createStore({
	initialState: {
		participants: new Map<string, PresenceParticipant>(),
		telepointers: new Map<string, TelepointerData>(),
	},
	actions: {
		add:
			(
				participant: ProviderParticipant,
			): (({
				getState,
				setState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void) =>
			({ getState, setState }) => {
				const { participants } = getState();

				// can't be null in confluence, ultimately is the userId for logged in users and presenceId for anonymous
				const { presenceId: id } = participant;
				const currentParticipants = new Map(participants);

				if (id) {
					const previousParticipant = currentParticipants.get(id);

					const entry: PresenceParticipant = {
						id,
						userId: participant.userId,
						presenceId: participant.presenceId,
						name: participant.name,
						isGuest: participant.isGuest,
						isCurrentUser: Boolean(previousParticipant?.isCurrentUser),
						src: participant.avatar,
						sessions: {
							...previousParticipant?.sessions,
							[participant.sessionId]: {
								activity: participant.presenceActivity ?? 'viewer',
							},
						},
					};

					currentParticipants.set(id, entry);
					setState({ participants: currentParticipants });
				} else {
					// eslint-disable-next-line no-console
					console.error(
						"presenceId is null for participant when it shouldn't be for ",
						participant,
					);
				}
			},
		remove:
			(
				sessionId: string,
			): (({
				getState,
				setState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void) =>
			({ getState, setState }) => {
				const { participants } = getState();
				const currentParticipants = new Map(participants);
				const participant = getCurrentUserFromMap(participants, sessionId);

				if (participant) {
					if (Object.keys(participant.sessions).length === 1) {
						currentParticipants.delete(participant.id);

						setState({
							participants: currentParticipants,
						});
					} else {
						delete participant.sessions[sessionId];

						currentParticipants.set(participant.id, participant);

						setState({
							participants: currentParticipants,
						});
					}
				} else {
					// eslint-disable-next-line no-console
					console.error('Could not remove participant with sessionId of ', sessionId);
				}
			},

		addTelepointerData:
			(
				data: CollabTelepointerPayload,
			): (({
				setState,
				getState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void) =>
			({ setState, getState }) => {
				const state = getState();
				const participant = getCurrentUserFromMap(state.participants, data.sessionId);

				if (
					// Don't track the telepointer data for the current user
					participant?.isCurrentUser
				) {
					return;
				}

				const telepointers = new Map(state.telepointers);
				telepointers.set(data.sessionId, { ...data, timestamp: Date.now() });
				setState({ telepointers });
			},
	},
	name: 'PresenceStore',
});

export const usePresenceStore: HookFunction<
	{
		participants: Map<string, PresenceParticipant>;
		telepointers: Map<string, TelepointerData>;
	},
	BoundActions<
		{
			participants: Map<string, PresenceParticipant>;
			telepointers: Map<string, TelepointerData>;
		},
		{
			add: (participant: ProviderParticipant) => ({
				getState,
				setState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void;
			remove: (sessionId: string) => ({
				getState,
				setState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void;
			addTelepointerData: (data: CollabTelepointerPayload) => ({
				setState,
				getState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void;
		}
	>,
	void
> = createHook(PresenceStore);
export const usePresenceStoreActions: HookActionsFunction<
	BoundActions<
		{
			participants: Map<string, PresenceParticipant>;
			telepointers: Map<string, TelepointerData>;
		},
		{
			add: (participant: ProviderParticipant) => ({
				getState,
				setState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void;
			remove: (sessionId: string) => ({
				getState,
				setState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void;
			addTelepointerData: (data: CollabTelepointerPayload) => ({
				setState,
				getState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void;
		}
	>
> = createActionsHook(PresenceStore);

export const useParticipants: HookStateFunction<
	Map<string, PresenceParticipant>,
	void
> = createStateHook(PresenceStore, {
	selector: (state) => state.participants,
});

export const getTransformedParticipants = (
	state: PresenceStoreState,
	props: { activity: PresenceActivity; sessionId: string; anonymousName: string },
): Participant[] => {
	const { participants } = state;

	const currentUser = getCurrentUserFromMap(participants, props.sessionId);

	const participantValues = [...participants.values()].filter(
		(p) => p.presenceId !== currentUser?.presenceId,
	);

	if (currentUser) {
		participantValues.unshift(currentUser);
	}

	return participantValues.map((participant) => {
		const isAnonymous = participant.userId === UNIDENTIFIED;

		// When the user is anonymous, we attempt to use the localized name from the anonymous asset or a default name
		const name = isAnonymous ? participant.name || props.anonymousName : participant.name;

		return {
			...participant,
			participantColor: isAnonymous
				? getParticipantColor(participant.id).color.backgroundColor
				: undefined,
			isCurrentUser: participant.isCurrentUser || participant.id === currentUser?.id,
			name,
			activity:
				participant.isCurrentUser && currentUser
					? getActivityToDisplay(participant.sessions, props.sessionId, props.activity)
					: getActivityToDisplay(participant.sessions),
		};
	});
};

export const useTransformedParticipants: HookStateFunction<
	Participant[],
	{
		activity: PresenceActivity;
		sessionId: string;
		anonymousName: string;
	}
> = createStateHook(PresenceStore, {
	selector: getTransformedParticipants,
});

// Telepointer data of the last active session based on the saved timestamp
export const getActiveTelepointerBySessions = (
	state: PresenceStoreState,
	sessions: PresenceSessionData,
): TelepointerData =>
	Object.keys(sessions)
		.map((sId) => state.telepointers.get(sId))
		.filter((tData): tData is TelepointerData => !!tData)
		.sort((a, b) => b?.timestamp - a?.timestamp)?.[0];

export const useActiveTelepointerBySessions: HookFunction<
	TelepointerData,
	BoundActions<
		{
			participants: Map<string, PresenceParticipant>;
			telepointers: Map<string, TelepointerData>;
		},
		{
			add: (participant: ProviderParticipant) => ({
				getState,
				setState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void;
			remove: (sessionId: string) => ({
				getState,
				setState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void;
			addTelepointerData: (data: CollabTelepointerPayload) => ({
				setState,
				getState,
			}: StoreActionApi<{
				participants: Map<string, PresenceParticipant>;
				telepointers: Map<string, TelepointerData>;
			}>) => void;
		}
	>,
	PresenceSessionData
> = createHook(PresenceStore, {
	selector: getActiveTelepointerBySessions,
});

export const PresenceStoreContainer: ContainerComponent<PresenceStoreContainerProps> =
	createContainer(PresenceStore, {
		onInit:
			() =>
			({ setState }, { participant, fetchAnonymousAsset, onError }) => {
				if (participant) {
					const defaultParticipants = new Map<string, PresenceParticipant>();

					if (participant.userId === UNIDENTIFIED) {
						fetchAnonymousAsset(participant.presenceId)
							.then((asset) => {
								defaultParticipants.set(participant.id, {
									...participant,
									src: asset?.src || participant.src,
									name: asset?.name || participant.name,
								});
							})
							.catch(onError)
							.finally(() => {
								setState({
									participants: defaultParticipants,
								});
							});
					} else {
						defaultParticipants.set(participant.id, {
							...participant,
						});
						setState({
							participants: defaultParticipants,
						});
					}
				} else {
					setState({
						participants: new Map(),
					});
				}
			},
	});
